import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useDebounce from '../../../hooks/useDebounce';
import {
    exportProjectAssociatedIssues,
    fetchProjectAssociatedIssues,
} from '../../../modules/associations';
import QUERY_KEYS from '../../../query/queryKeys';
import TableComponent from '../../../ui/data/table/TableComponent';
import CustomSelect from '../../../ui/primitives/CustomSelect';
import SearchBar from '../../../ui/primitives/SearchBar';
import { RefreshButton } from '../../elements/CustomButtons';

const FILTER_NOT_AVAILABLE_MSG = "This filter isn’t available for your account yet. Please contact your administrator to enable it.";

const ProjectIssuesComponent = ({ isAdmin }) => {
    const { projectId } = useParams();
    const queryClient = useQueryClient();
    const forceRefreshRef = useRef(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [order, setOrder] = useState('desc');
    const [isExporting, setIsExporting] = useState(false);

    const [filters, setFilters] = useState({
        assignee: 'me',
        status: 'all',
        priority: '',
        search: '',
    });

    const [searchValue, setSearchValue] = useState(filters.search);
    const debouncedSearchQuery = useDebounce(searchValue, 500);

    useEffect(() => {
        setFilters((prev) => ({ ...prev, search: debouncedSearchQuery }));
        setPage(1);
    }, [debouncedSearchQuery]);

    const {
        data: issuesData,
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [
            QUERY_KEYS.project.jiraIssues,
            projectId,
            page,
            perPage,
            order,
            JSON.stringify(filters),
        ],
        queryFn: async () => {
            const forceRefresh = forceRefreshRef.current;
            forceRefreshRef.current = false;

            const normalizedFilters = {
                ...filters,
                assignee: filters.assignee === 'all' ? undefined : filters.assignee,
                status: filters.status === 'all' ? undefined : filters.status,
            };

            return fetchProjectAssociatedIssues(projectId, {
                page,
                pageSize: perPage,
                sortOrder: order,
                forceRefresh,
                ...normalizedFilters,
            });
        },
        enabled: !!projectId,
        placeholderData: (prev) => prev,
    });

    const handleRefresh = () => {
        forceRefreshRef.current = true;
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.project.jiraIssues],
        });
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const blob = await exportProjectAssociatedIssues(projectId, {
                assignee: filters.assignee === 'all' ? undefined : filters.assignee,
                status: filters.status === 'all' ? undefined : filters.status,
                search: filters.search,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'project-issues.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
        }
        setIsExporting(false);
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);

        // If user manually changes search, also update searchValue
        if (key === 'search') setSearchValue(value);
    };

    const formattedIssues = useMemo(() => {
        return (issuesData?.issues || []).map((issue) => ({
            id: issue.jiraIssueId,
            key: issue.key,
            summary: issue.summary,
            reporter: issue.reporter,
            priority: issue.priority,
            status: issue.status,
            assignee: issue.assignee?.jira_name,
            siteUrl: issue.site_url,
        }));
    }, [issuesData]);

    const headers = useMemo(
        () => [
            {
                key: 'key',
                label: 'Issue Key',
                formatContent: (row) => (
                    <a
                        href={`${row.siteUrl}/browse/${row.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {row.key}
                    </a>
                ),
            },
            {
                key: 'summary',
                label: 'Summary',
                formatContent: (row) => (
                    <span className="m-0" title={row.summary}>
                        {row.summary?.length > 100
                            ? `${row.summary.slice(0, 100)}...`
                            : row.summary}
                    </span>
                ),
            },
            {
                key: 'priority',
                label: 'Priority',
                formatContent: (row) => (
                    <span className="badge bg-light text-dark text-uppercase">
                        {row.priority}
                    </span>
                ),
            },
            {
                key: 'status',
                label: 'Status',
                formatContent: (row) => (
                    <span className="badge bg-light text-dark text-uppercase">
                        {row.status}
                    </span>
                ),
            },
            {
                key: 'reporter',
                label: 'Reporter',
                formatContent: (row) =>
                    row.reporter ? (
                        <span>{row.reporter}</span>
                    ) : (
                        <span className="text-muted" style={{ fontSize: '14px' }}>
                            Unknown
                        </span>
                    ),
            },
            {
                key: 'assignee',
                label: 'Assignee',
                formatContent: (row) =>
                    row.assignee ? (
                        <span>{row.assignee}</span>
                    ) : (
                        <span className="text-muted" style={{ fontSize: '14px' }}>
                            Unassigned
                        </span>
                    ),
            },
        ],
        []
    );

    const getErrorMessage = () => {
        if (error.message === 'Session not found') {
            return 'Jira is not integrated';
        }
        if (error.message === FILTER_NOT_AVAILABLE_MSG && isAdmin) {
            return (
                <>
                    Your account isn't mapped to a Jira user, which is required for this filter.
                    <br />
                    Configure the mapping {' '}
                    <a
                        href={`/project/${projectId}/configurations?tab=jira`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <OpenInNewIcon fontSize="small" />
                    </a>
                </>
            );
        }
        return error.message;
    };

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Typography variant="h4" className="mb-2">
                    Project Issues
                </Typography>

                <Grid container alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                    <Grid item xs>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <SearchBar
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                clearSearch={() => setSearchValue('')}
                                placeholder="Search by key or summary..."
                                style={{ minWidth: 250 }}
                            />
                            <CustomSelect
                                id="assignee-filter"
                                value={filters.assignee}
                                onChange={(value) => handleFilterChange('assignee', value)}
                                options={[
                                    { id: 'me', name: 'Assigned to Me' },
                                    { id: 'all', name: 'All Assignees' },
                                ]}
                                sx={{ minWidth: 180 }}
                                placeholder="Filter by Assignee"
                            />

                            <CustomSelect
                                id="status-filter"
                                value={filters.status}
                                onChange={(value) => handleFilterChange('status', value)}
                                options={[
                                    { id: 'all', name: 'All Statuses' },
                                    { id: 'Open', name: 'Open' },
                                    { id: 'Done', name: 'Done' },
                                ]}
                                sx={{ minWidth: 160 }}
                                placeholder="Filter by Status"
                            />
                        </Stack>
                    </Grid>

                    {/* RIGHT: Actions */}
                    <Grid item>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                disabled={!issuesData?.total || isExporting}
                                onClick={handleExport}
                            >
                                {isExporting ? 'Exporting...' : 'Export'}
                            </Button>

                            {!isLoading && (
                                <RefreshButton
                                    onRefresh={handleRefresh}
                                    // isFetching={isFetching}
                                    showLastUpdated={false}
                                    buttonSize={24}
                                    width={34}
                                />
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                {isError ? (
                    <div className="text-center" style={{ marginTop: '15vh', color: '#5c6bc0' }}>
                        <div style={{ fontSize: '48px' }}>⚠️</div>
                        <div style={{ fontSize: '16px', fontWeight: 500, marginTop: '8px' }}>
                            {getErrorMessage()}
                        </div>
                    </div>
                ) : (
                    <TableComponent
                        data={formattedIssues}
                        headerTabs={headers}
                        isLoading={isLoading || isFetching}
                        page={page}
                        perPage={perPage}
                        totalCount={issuesData?.total || 0}
                        handlePageChange={setPage}
                        order={order}
                        setOrder={setOrder}
                        setPerPage={(e) => {
                            setPerPage(parseInt(e.target.value, 10));
                            setPage(1);
                        }}
                        tablekey="project issues"
                        showCheckbox={false}
                        showCount
                        showRowCount
                        showPages
                        serverSideSorting
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default ProjectIssuesComponent;